import axios from "axios";
import store from "@/store";
import { getToken, getRefreshToken } from "@/utils";
import config from "@/config";
import { message } from "ant-design-vue";
import {myMsgBox} from "@/components/globalModal/modalService.js";

// 创建 axios 实例
const myRequest = axios.create({
  // API 请求的默认前缀
  baseURL: process.env.NODE_ENV === "production" || process.env.VUE_APP_BUILD == '1' ? config.baseUrl : "/api",
  timeout: 30000, // 请求超时时间
});

// 刷新token相关状态
let isRefreshing = false;
let refreshSubscribers = [];

// 处理刷新token完成后的回调
const onRefreshed = (token) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// 添加刷新回调
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// 异常拦截处理器
const errorHandler = async (error) => {
  let code = 0;
  if (error.data && error.data.status) {
    code = error.data.status;
  } else if (error.data && error.data.code) {
    code = error.data.code;
  } else if (error?.response?.status) {
    code = error.response.status;
  }
  
  if (code) {
    if (code === 500) {
      message.error("服务器错误！");
    }
    if (code === 403) {
      console.log(403);
    }
    if (code === 401) {
      // 检查是否是RefreshToken请求本身返回的401错误
      const isRefreshTokenRequest = error.config?.url?.includes('/oauth2/token') && 
                                   error.config?.data?.includes('grant_type=refresh_token');
      if (isRefreshTokenRequest) {
        // RefreshToken接口返回401，说明refreshToken已失效，直接跳转登录
        message.error("登录已过期，请重新登录！");
        store.dispatch("Logout");
        return Promise.reject(error);
      }
      // 防止重复刷新
      if (!isRefreshing) {
        isRefreshing = true;
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
          // 没有refreshToken，直接跳转登录
          message.error("登录已过期，请重新登录！");
          isRefreshing = false;
          store.dispatch("Logout");
          return Promise.reject(error);
        }
        
        try {
          const params = new URLSearchParams();
          params.append('grant_type', 'refresh_token');
          params.append('refresh_token', refreshToken);
          
          // 调用RefreshToken接口
          await store.dispatch("RefreshToken", params);
          
          // 刷新成功，执行所有等待的请求
          onRefreshed(getToken());
          isRefreshing = false;
          
          // 重新发送当前请求
          return myRequest(error.config);
        } catch (refreshError) {
          // 刷新token失败
          isRefreshing = false;
          refreshSubscribers = [];
          // 其他错误（如网络错误等）
          message.error("刷新token失败，请重新登录！");
          store.dispatch("Logout");
          return Promise.reject(refreshError);
        }
      } else {
        // 正在刷新中，将当前请求加入等待队列
        return new Promise((resolve, reject) => {
          // 添加超时机制，防止无限等待
          const timeoutId = setTimeout(() => {
            const index = refreshSubscribers.indexOf(callback);
            if (index > -1) {
              refreshSubscribers.splice(index, 1);
            }
            message.error("请求超时，请重新登录！");
            store.dispatch("Logout");
            reject(new Error('Token refresh timeout'));
          }, 10000); // 10秒超时
          
          const callback = (token) => {
            clearTimeout(timeoutId);
            // 使用新的token重新发送请求
            error.config.headers['Authorization'] = `Bearer ${token}`;
            resolve(myRequest(error.config));
          };
          
          addRefreshSubscriber(callback);
        });
      }
    }
    // 412重置密码
    if (code == 412) {
      if (!document.getElementsByClassName("password-modal-container")[0]) {
        myMsgBox();
      }
    }
  }
  if (error.response) {
    return Promise.reject(error.response);
  }
  return Promise.reject(error);
};

// request interceptor
myRequest.interceptors.request.use((config) => {
  const token = getToken();
  // 如果 token 存在
  // 让每个请求携带自定义 token 请根据实际情况自行修改
  config.headers["source"] = "operating-platform-web";
  config.headers["Web-Type"] = "manage";
  if (token && !config.isWithoutToken) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
}, errorHandler);

// response interceptor
myRequest.interceptors.response.use((response) => {
  if (
    (typeof response.data == "object" &&
      response.data.hasOwnProperty("code") &&
      response.data.code !== 0) ||
    (response.data.status && response.data.status != 200) ||
    response.data.data === false
  ) {
    // 需自定义
    return errorHandler(response);
  }
  return response;
}, errorHandler);

export const download = (config) => {
  return new Promise((resolve, reject) => {
    myRequest({ ...config, responseType: "blob", timeout: 60 * 1000 })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err.data);
      });
  });
};

export const request = (config) => {
  return new Promise((resolve, reject) => {
    if (config.url.indexOf(".json") != -1) {
      config.url = `${window.location.origin}/json${config.url}`;
      config.method = "get";
      // console.log(JSON.stringify(config.data));
    }
    myRequest(config)
      .then((res) => {
        resolve(res.data);
      })
      .catch((err) => {
        reject(err.data);
      });
  });
};

export default request;